import React, { useState, useEffect, useCallback } from 'react';

import { Modal, ModalDialog, ModalClose, Typography, Button, Alert, Stack, Link } from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import OtpInput from 'react-otp-input';

import { makeAuthenticatedPostRequest } from '../services/axios';
import {
  sendVerificationOtp,
  resendVerificationOtp,
  verifyEmailOtp,
} from '../services/axios/endpoints';
import { MAX_OTP_RESEND_ATTEMPTS, OTP_LENGTH } from '../utils/constants/emailVerification';
import { useLogout } from '../utils/hooks/useLogout';

interface ApiResponse {
  message?: string;
  resent_count?: number;
}

interface MutationResponse {
  data: ApiResponse;
  status: number;
}

interface EmailReVerificationModalProps {
  open: boolean;
  onSuccess: () => void;
  userEmail: string;
  resentCount: number;
  setShowModal: (show: boolean) => void;
}

const EmailReVerificationModal: React.FC<EmailReVerificationModalProps> = ({
  open,
  onSuccess,
  userEmail,
  resentCount: initialResentCount,
  setShowModal,
}) => {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resentCount, setResentCount] = useState(initialResentCount);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [hasInitialCodeSent, setHasInitialCodeSent] = useState(false);
  const logout = useLogout();

  const isResendDisabled = resentCount >= MAX_OTP_RESEND_ATTEMPTS;
  const isCooldownActive = cooldownSeconds > 0;

  const clearMessages = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  const focusFirstInput = useCallback(() => {
    setTimeout(() => {
      const firstInput = document.querySelector(
        'input[data-testid="otp-input-0"]'
      ) as HTMLInputElement;
      if (firstInput) {
        firstInput.focus();
      }
    }, 100);
  }, []);

  const handleApiResponse = useCallback(
    ({ data, status }: MutationResponse, isSuccess: boolean) => {
      if (status >= 200 && status < 300) {
        setSuccess(data.message || '');
        setError('');
        if (data.resent_count !== undefined) {
          setResentCount(data.resent_count);
        }
        if (isSuccess) {
          setCooldownSeconds(30);
        }
      } else {
        setError(data.message || '');
        setSuccess('');
      }
    },
    []
  );

  const handleApiError = useCallback((error: unknown) => {
    const errorMessage =
      (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
      'An error occurred';
    setError(errorMessage);
    setSuccess('');
  }, []);

  const sendOtpMutation = useMutation({
    mutationFn: async (): Promise<MutationResponse> => {
      const response = await makeAuthenticatedPostRequest(sendVerificationOtp, {
        email: userEmail,
      });
      return { data: response.data, status: response.status };
    },
    onSuccess: (response) => handleApiResponse(response, true),
    onError: handleApiError,
  });

  const verifyOtpMutation = useMutation({
    mutationFn: async (otpCode: string): Promise<MutationResponse> => {
      const response = await makeAuthenticatedPostRequest(verifyEmailOtp, { otp: otpCode });
      return { data: response.data, status: response.status };
    },
    onSuccess: ({ data, status }) => {
      if (status >= 200 && status < 300) {
        setShowModal(false);
        setSuccess(data.message || '');
        setError('');
        onSuccess();
      } else {
        setError(data.message || '');
        setSuccess('');
        setOtp('');
        focusFirstInput();
      }
    },
    onError: (error: unknown) => {
      handleApiError(error);
      setOtp('');
      focusFirstInput();
    },
  });

  useEffect(() => {
    if (open) {
      setOtp('');
      setCooldownSeconds(0);
      setHasInitialCodeSent(false);
      clearMessages();
    }
  }, [open, clearMessages]);

  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => setCooldownSeconds((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  const resendOtpMutation = useMutation({
    mutationFn: async (): Promise<MutationResponse> => {
      const response = await makeAuthenticatedPostRequest(resendVerificationOtp, {
        email: userEmail,
      });
      return { data: response.data, status: response.status };
    },
    onSuccess: (response) => handleApiResponse(response, true),
    onError: handleApiError,
  });

  const handleResendOtp = useCallback(() => {
    if (isResendDisabled) {
      return;
    }
    setOtp('');
    clearMessages();
    resendOtpMutation.mutate();
  }, [isResendDisabled, clearMessages, resendOtpMutation]);

  const handleOtpChange = useCallback(
    (value: string) => {
      setOtp(value);
      clearMessages();

      if (value.length === OTP_LENGTH && /^\d+$/.test(value) && !verifyOtpMutation.isPending) {
        verifyOtpMutation.mutate(value);
      }
    },
    [clearMessages, verifyOtpMutation]
  );

  const handleSendOtp = useCallback(() => {
    setOtp('');
    clearMessages();
    setHasInitialCodeSent(true);
    sendOtpMutation.mutate();
  }, [clearMessages, sendOtpMutation]);

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={() => undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ModalDialog
        variant='outlined'
        sx={{
          width: '90%',
          maxWidth: 480,
          p: 4,
          boxShadow: 'lg',
        }}
      >
        <ModalClose sx={{ display: 'none' }} />
        <Typography level='h3' textAlign='center' mb={1}>
          {hasInitialCodeSent
            ? 'Enter your verification code'
            : 'Verify your account for continued access'}
        </Typography>
        {hasInitialCodeSent ? (
          <Typography level='body-md' textAlign='center' mb={3} color='neutral'>
            We've sent a {OTP_LENGTH}-digit verification code to <strong>{userEmail}</strong>. Check
            your inbox (and spam folder) and enter it below to continue.
          </Typography>
        ) : (
          <Typography level='body-md' textAlign='center' mb={3} color='neutral'>
            As part of our regular security check, we need to quickly confirm this account is still
            in use. Please click "Send code" to receive a {OTP_LENGTH}-digit verification code at{' '}
            <strong>{userEmail}</strong> and finalize your login.
          </Typography>
        )}
        {hasInitialCodeSent && (
          <Stack spacing={3} mb={3}>
            <Typography level='body-sm' textAlign='center'>
              Enter verification code:
            </Typography>
            <Stack direction='row' justifyContent='center' spacing={1}>
              <OtpInput
                value={otp}
                onChange={handleOtpChange}
                numInputs={OTP_LENGTH}
                renderInput={(props, index) => (
                  <input
                    {...props}
                    data-testid={`otp-input-${index}`}
                    disabled={sendOtpMutation.isPending}
                    style={{
                      width: '48px',
                      height: '48px',
                      fontSize: '18px',
                      fontWeight: '600',
                      borderRadius: 'var(--joy-radius-sm)',
                      border: '1px solid var(--joy-palette-neutral-300)',
                      textAlign: 'center',
                      outline: 'none',
                      backgroundColor: sendOtpMutation.isPending
                        ? 'var(--joy-palette-neutral-100)'
                        : 'var(--joy-palette-background-surface)',
                      color: sendOtpMutation.isPending
                        ? 'var(--joy-palette-neutral-500)'
                        : 'var(--joy-palette-text-primary)',
                      cursor: sendOtpMutation.isPending ? 'not-allowed' : 'text',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: 'var(--joy-shadow-sm)',
                      marginRight: '8px',
                    }}
                  />
                )}
              />
            </Stack>
          </Stack>
        )}
        {error && (
          <Alert color='danger' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert color='success' sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        <Stack spacing={2}>
          {!hasInitialCodeSent && (
            <Button
              variant='solid'
              size='lg'
              fullWidth
              onClick={handleSendOtp}
              disabled={sendOtpMutation.isPending}
              loading={sendOtpMutation.isPending}
            >
              Send verification code
            </Button>
          )}

          {hasInitialCodeSent && (
            <Button
              variant='plain'
              color='neutral'
              size='md'
              fullWidth
              onClick={handleResendOtp}
              disabled={isResendDisabled || resendOtpMutation.isPending || isCooldownActive}
              loading={resendOtpMutation.isPending}
            >
              {isResendDisabled
                ? 'Resend limit reached'
                : isCooldownActive
                  ? `Re-send code (${cooldownSeconds}s)`
                  : 'Re-send code'}
            </Button>
          )}
        </Stack>
        {resentCount > 0 && (
          <Typography level='body-xs' textAlign='center' mt={2} color='neutral'>
            Resend attempts: {resentCount}/{MAX_OTP_RESEND_ATTEMPTS}
          </Typography>
        )}
        <Stack direction='row' justifyContent='center' alignItems='center' spacing={1} mt={2}>
          <Typography level='body-xs' color='neutral' textAlign={'center'}>
            {hasInitialCodeSent
              ? 'Wrong email? '
              : "Didn't get the email? Check your spam/junk folder. | Wrong email?"}
            <Link
              component='button'
              level='body-xs'
              onClick={logout}
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              Go back to login
            </Link>
          </Typography>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default EmailReVerificationModal;
